'use client';
import { useEffect, useRef } from 'react';
import type { BasisStructure } from 'types/cms';

export interface WidgetIntegrationDefaultProps extends BasisStructure {
  readonly model: {
    readonly data?: string;
  };
  readonly title: string;
}

export const WidgetIntegrationDefault = ({ context }: { context: WidgetIntegrationDefaultProps }) => {
  const { model, title } = context;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'resize-iframe') return;

      if (iframeRef.current && event.source === iframeRef.current.contentWindow) {
        iframeRef.current.style.height = `${event.data.height + 20}px`;
        return;
      }

      if (containerRef.current) {
        const iframes = containerRef.current.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
          if (event.source === iframe.contentWindow) {
            iframe.style.height = `${event.data.height + 20}px`;
          }
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (!model.data) return null;

  const hasIframe = /<iframe\b[^>]*>/i.test(model.data);

  if (hasIframe) {
    return <div ref={containerRef} data-testid={title} dangerouslySetInnerHTML={{ __html: model.data }} />;
  }

  const resizeScript = `
    <script>
      (function() {
        const sendHeight = () => {
          const height = document.body.scrollHeight;
          window.parent.postMessage({ type: 'resize-iframe', height }, '*');
        };
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', sendHeight);
        } else {
          sendHeight();
        }
        
        if (window.ResizeObserver) {
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        }
        
        setInterval(sendHeight, 1000);
      })();
    </script>
  `;

  const contentWithResize = model.data + resizeScript;

  return (
    <iframe
      ref={iframeRef}
      data-testid={title}
      sandbox="allow-scripts allow-same-origin allow-popups"
      srcDoc={contentWithResize}
      style={{ width: '100%', border: 'none', minHeight: '100px' }}
      title={title}
    />
  );
};
